import { FC, useEffect, useMemo, useState } from 'react';
import { Content as Wrapper } from '../../../../../../../../shared/components/content/content.component.tsx';
import { FormProvider, Resolver, useForm } from 'react-hook-form';
import {
  FormTextField,
  FieldsSection,
  FieldsRow,
  FormSelect,
} from '../../../../../../../../shared/components/form';
import { Stack, Box, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AlternateEmail, Fax, Language, Phone } from '@mui/icons-material';
import { IPopupWrapper } from '../../../../../../../../shared/components/popup/components/popup.component.tsx';
import {
  dictionaryAdditionalService,
  GetDictSubCompanyRes,
} from '../../../../../../../../shared/components/dictionary/services/dictionaryAdditional.service.ts';
import { responseHandler } from '../../../../../../../../shared/responseHandler/responseHandler.ts';
import { take } from 'rxjs';
import { dictionaryState } from '../../../../../../../../shared/components/dictionary/states/dictionary.state.ts';
import { useBeforeClosePopup } from '../../../../../../../../shared/components/popup/hooks/useBeforeClosePopup.tsx';
import { SForm } from '../../../components/companyForm.component.tsx';
import { C_Country } from '../../../../../../../../graphql/generatedModel.ts';
import { yupResolver } from '@hookform/resolvers/yup';
import { createValidationSchema } from '../../../company.schema.ts';
import { Footer } from '../../../../../../../../shared/components/popup/components/footer.component.tsx';

const validationSchema = createValidationSchema();

export const Content: FC<IContent> = ({ stream }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [id, setId] = useState<string>('');

  const formMethods = useForm<GetDictSubCompanyRes>({
    mode: 'onSubmit',
    defaultValues: {
      companySalutation: '',
      companyName: '',
      companyAddition: '',
      companyStreet: '',
      companyZipCode: '',
      companyCity: '',
      companyPhone: '',
      companyFax: '',
      companyEmail: '',
      companyWebsite: '',
      companyGLN: '',
      companyVatNo: '',
      countryId: C_Country.CO1_SWITZERLAND,
      bankName: '',
      bankBIN: '',
      bankAccountNo: '',
      bankIBAN: '',
      creditorIdNumber: '',
      bankBIC: '',
    },
    resolver: yupResolver(validationSchema) as unknown as Resolver<GetDictSubCompanyRes>,
  });
  const {
    watch,
    formState: { errors, dirtyFields, isDirty },
  } = formMethods;

  const countryOptions = useMemo(() => {
    return Object.keys(C_Country).map((item) => {
      return { id: item, label: t(`enums.${item}`) };
    });
  }, [C_Country, t]);

  const countryId = watch('countryId');

  // VAT number setting visible for European countries
  const vatNumberVisibility = countryId !== C_Country.CO4_MALDIVES;

  // Bank account settings visible for European countries except Switzerland (it has BESR banks settings)
  const bankVisibility =
    countryId !== C_Country.CO1_SWITZERLAND && countryId !== C_Country.CO4_MALDIVES;

  useEffect(() => {
    const dictionaryAdditionalSub = dictionaryAdditionalService.sub
      .getDictSubCompany()
      .pipe(
        responseHandler<GetDictSubCompanyRes>({
          errorReturnType: {
            id: '',
            countryId: C_Country.CO1_SWITZERLAND,
          },
        }),
      )
      .subscribe((formData) => {
        formMethods.reset(formData);
        setLoading(false);
      });

    stream
      .actionListener('open')
      .pipe(take(1))
      .subscribe(({ id }) => {
        dictionaryAdditionalService.pub.getDictSubCompany({ id });
        setId(id);
      });
    return () => dictionaryAdditionalSub.unsubscribe();
  }, []);

  const onSubmit = (formData: GetDictSubCompanyRes) => {
    if (Object.keys(dirtyFields).length) {
      dictionaryState.pub.updateCell(
        {
          id,
          field: 'virtualField',
          value: formData,
        },
        true,
      );
    }
    stream.emit('close');
  };

  useBeforeClosePopup({
    stream,
    isDirty,
  });

  return (
    <Wrapper type='box' {...{ loading }} boxProps={{ sx: { minHeight: '589px' } }}>
      <FormProvider {...formMethods}>
        <SForm
          id={'subCompanyForm'}
          style={{ opacity: loading ? '0' : '1', transition: '0.3s' }}
          onSubmit={formMethods.handleSubmit(onSubmit)}
          noValidate
        >
          {!loading && (
            <>
              <FieldsSection
                titleBoxSx={{ pb: 2, pt: 0 }}
                nomt
                title={t('settings.company.companySection')}
                className='company'
              >
                <Stack spacing={3}>
                  <FormTextField
                    slotProps={{ htmlInput: { maxLength: 50 } }}
                    width='100%'
                    name='companySalutation'
                    label={t('settings.company.company.label')}
                  />
                  <FormTextField
                    slotProps={{ htmlInput: { maxLength: 50 } }}
                    width='100%'
                    name='companyName'
                    label={t('settings.company.companyName.label')}
                  />
                  <FormTextField
                    slotProps={{ htmlInput: { maxLength: 50 } }}
                    width='100%'
                    name='companyAddition'
                    label={t('settings.company.addition.label')}
                  />
                  <FormTextField
                    slotProps={{ htmlInput: { maxLength: 50 } }}
                    width='100%'
                    name='companyStreet'
                    label={t('settings.company.street.label')}
                  />
                  <FieldsRow>
                    <FormTextField
                      slotProps={{ htmlInput: { maxLength: 10 } }}
                      width='100%'
                      name='companyZipCode'
                      label={t('settings.company.zip.label')}
                    />
                    <FormTextField
                      slotProps={{ htmlInput: { maxLength: 50 } }}
                      width='100%'
                      name='companyCity'
                      label={t('settings.company.city.label')}
                    />
                  </FieldsRow>
                </Stack>
              </FieldsSection>
              <FieldsSection titleBoxSx={{ pb: 2, pt: 0 }} nomt title={' '} className='company2'>
                <Stack spacing={3}>
                  <FormTextField
                    slotProps={{
                      htmlInput: { maxLength: 50 },
                      input: { endAdornment: <Phone sx={{ color: 'rgba(0, 0, 0, 0.54)' }} /> },
                    }}
                    name='companyPhone'
                    label={t('settings.company.phone.label')}
                    width='100%'
                  />

                  <FormTextField
                    slotProps={{
                      htmlInput: { maxLength: 50 },
                      input: { endAdornment: <Fax sx={{ color: 'rgba(0, 0, 0, 0.54)' }} /> },
                    }}
                    name='companyFax'
                    label={t('settings.company.fax.label')}
                    width='100%'
                  />

                  <FormTextField
                    slotProps={{
                      htmlInput: { maxLength: 50 },
                      input: {
                        endAdornment: <AlternateEmail sx={{ color: 'rgba(0, 0, 0, 0.54)' }} />,
                      },
                    }}
                    name='companyEmail'
                    label={t('common.email')}
                    width='100%'
                    error={Boolean(errors['companyEmail'])}
                    helperText={
                      errors['companyEmail'] ? t(String(errors['companyEmail'].message)) : ''
                    }
                  />

                  <FormTextField
                    slotProps={{
                      htmlInput: { maxLength: 50 },
                      input: { endAdornment: <Language sx={{ color: 'rgba(0, 0, 0, 0.54)' }} /> },
                    }}
                    name='companyWebsite'
                    label={t('settings.company.website.label')}
                    width='100%'
                  />
                  <FormTextField
                    slotProps={{ htmlInput: { maxLength: 13 } }}
                    width='100%'
                    name='companyGLN'
                    label={t('settings.company.gln.label')}
                  />
                  <Box sx={{ minHeight: '40px' }}>
                    {vatNumberVisibility && (
                      <FormTextField
                        slotProps={{ htmlInput: { maxLength: 100 } }}
                        width='100%'
                        name='companyVatNo'
                        label={t(
                          countryId === C_Country.CO1_SWITZERLAND
                            ? 'settings.company.vat.label'
                            : 'settings.company.uid.label',
                        )}
                      />
                    )}
                  </Box>
                </Stack>
              </FieldsSection>

              <FieldsSection
                titleBoxSx={{ pb: 2, pt: 0 }}
                nomt
                title={t('settings.company.countrySection')}
                className='country'
              >
                <FormSelect
                  name='countryId'
                  width='100%'
                  options={countryOptions}
                  disableClearable
                  disableTextInput
                />
              </FieldsSection>
              {bankVisibility && (
                <>
                  <FieldsSection
                    titleBoxSx={{ pb: 2, pt: 0 }}
                    nomt
                    title={t('settings.company.bankSection')}
                    className='bank'
                  >
                    <Stack spacing={3}>
                      <FormTextField
                        slotProps={{ htmlInput: { maxLength: 32 } }}
                        width='100%'
                        name='bankName'
                        label={t('settings.company.bankName.label')}
                      />
                      <FormTextField
                        slotProps={{ htmlInput: { maxLength: 16 } }}
                        width='100%'
                        name='bankBIN'
                        label={t('settings.company.bin.label')}
                      />
                      <FormTextField
                        slotProps={{ htmlInput: { maxLength: 32 } }}
                        width='100%'
                        name='bankAccountNo'
                        label={t('settings.company.accountNo.label')}
                      />
                    </Stack>
                  </FieldsSection>
                  <FieldsSection titleBoxSx={{ pb: 2, pt: 0 }} nomt title={''} className='bank2'>
                    <Stack spacing={3}>
                      <FormTextField
                        slotProps={{ htmlInput: { maxLength: 32 } }}
                        width='100%'
                        name='bankIBAN'
                        label={t('settings.company.iban.label')}
                      />
                      <FormTextField
                        slotProps={{ htmlInput: { maxLength: 40 } }}
                        width='100%'
                        name='creditorIdNumber'
                        label={t('settings.company.cin.label')}
                      />
                      <FormTextField
                        slotProps={{ htmlInput: { maxLength: 32 } }}
                        width='100%'
                        name='bankBIC'
                        label={t('settings.company.bic.label')}
                      />
                    </Stack>
                  </FieldsSection>
                </>
              )}
            </>
          )}
        </SForm>
      </FormProvider>
      {!loading && (
        <form id={'subCompanyForm'} onSubmit={formMethods.handleSubmit(onSubmit)}>
          <Footer>
            <Box
              sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', marginTop: '20px' }}
            >
              <Button type='submit' variant='contained' sx={{ width: '101px' }}>
                {t('common.save')}
              </Button>
            </Box>
          </Footer>
        </form>
      )}
    </Wrapper>
  );
};

interface IContent {
  stream: IPopupWrapper['stream'];
}
